import type {
  MomentButton,
  MomentCarouselItem,
  MomentCommerceIntro,
  MomentCoupon,
  MomentImageAsset,
  MomentListItem,
  MomentMessageType,
  MomentMoreButton,
} from "../data/momentOptions";
import { getMaxButtons } from "../data/momentOptions";

export const normalizeUrl = (value: string) => value.trim();
export const resolvePcUrl = (pcUrl: string, mobileUrl: string) => normalizeUrl(pcUrl) || normalizeUrl(mobileUrl);
export const createMomentImageName = (timestamp: string, seq: number) => `${timestamp}_${seq}`;

export function buildCardButtons(item: MomentCarouselItem, messageType?: MomentMessageType) {
  const btn1Title = messageType === "CAROUSEL_COMMERCE_MESSAGE" ? "구매하기" : item.buttonTitle.trim();
  const btn1Mobile = messageType === "CAROUSEL_COMMERCE_MESSAGE"
    ? normalizeUrl(item.mobileLandingUrl)
    : normalizeUrl(item.buttonMobileLandingUrl);
  if (!btn1Title || !btn1Mobile) return undefined;

  const btn1 = {
    title: btn1Title,
    pcLandingUrl: messageType === "CAROUSEL_COMMERCE_MESSAGE"
      ? resolvePcUrl(item.pcLandingUrl, item.mobileLandingUrl)
      : resolvePcUrl(item.buttonPcLandingUrl, item.buttonMobileLandingUrl),
    mobileLandingUrl: btn1Mobile,
  };

  if (item.shareFlag) return [btn1];
  if (!item.secondaryButtonEnabled) return [btn1];

  const btn2Title = item.secondaryButtonTitle.trim();
  const btn2Mobile = normalizeUrl(item.secondaryButtonMobileLandingUrl);
  if (!btn2Title || !btn2Mobile) return [btn1];

  return [
    btn1,
    {
      title: btn2Title,
      pcLandingUrl: resolvePcUrl(item.secondaryButtonPcLandingUrl, item.secondaryButtonMobileLandingUrl),
      mobileLandingUrl: btn2Mobile,
    },
  ];
}

export type BuildMessageBodyParams = {
  messageType: MomentMessageType;
  adFlag: boolean;
  messageBody: string;
  messageTitle: string;
  shareFlag: boolean;
  buttons: MomentButton[];
  couponEnabled: boolean;
  coupon: MomentCoupon;
  moreButtonEnabled: boolean;
  landingPcUrl: string;
  landingMobileUrl: string;
  moreButton: MomentMoreButton;
  images: {
    wideImage: MomentImageAsset;
    listItems: MomentListItem[];
    carouselItems: MomentCarouselItem[];
    commerceIntro: MomentCommerceIntro;
  };
  commerceIntroEnabled: boolean;
};

export function buildMessageBody(params: BuildMessageBodyParams): Record<string, unknown> {
  const {
    messageType, adFlag, messageBody, messageTitle, shareFlag,
    buttons, couponEnabled, coupon, moreButtonEnabled, landingPcUrl, landingMobileUrl,
    moreButton, images, commerceIntroEnabled,
  } = params;

  const base: Record<string, unknown> = { type: messageType, adFlag: true };

  const maxButtons = getMaxButtons(messageType);
  const filteredButtons = buttons
    .slice(0, maxButtons)
    .filter((b) => b.title.trim() !== "");
  const mapButtons = (list: typeof filteredButtons) =>
    list.map((b) => ({
      title: b.title.trim(),
      mobileLandingUrl: normalizeUrl(b.mobileLandingUrl),
      ...(b.pcLandingUrl.trim() ? { pcLandingUrl: normalizeUrl(b.pcLandingUrl) } : {}),
    }));

  const buildCouponBook = () => ({
    ...coupon,
    mobileLandingUrl: normalizeUrl(coupon.mobileLandingUrl),
    ...(coupon.pcLandingUrl.trim() ? { pcLandingUrl: normalizeUrl(coupon.pcLandingUrl) } : {}),
  });

  if (messageType === "BASIC_TEXT_MESSAGE") {
    return {
      ...base,
      description: messageBody,
      shareFlag,
      ...(filteredButtons.length > 0 ? { buttons: mapButtons(filteredButtons) } : {}),
      ...(couponEnabled ? { couponBook: buildCouponBook() } : {}),
    };
  }

  if (messageType === "WIDE_MESSAGE") {
    return {
      ...base,
      shareFlag,
      ...(messageTitle.trim() ? { messageTitle } : {}),
      description: messageBody,
      items: [
        {
          imageUrl: images.wideImage.imageUploadedUrl,
          pcLandingUrl: resolvePcUrl(landingPcUrl, landingMobileUrl),
          mobileLandingUrl: normalizeUrl(landingMobileUrl),
        },
      ],
      ...(filteredButtons.length > 0 ? { buttons: mapButtons(filteredButtons) } : {}),
      ...(couponEnabled ? { couponBook: buildCouponBook() } : {}),
    };
  }

  if (messageType === "WIDE_LIST_MESSAGE") {
    return {
      ...base,
      shareFlag,
      messageTitle,
      items: images.listItems.map((item) => ({
        description: item.description,
        imageUrl: item.imageUploadedUrl,
        landing: {
          pcLandingUrl: resolvePcUrl(item.pcLandingUrl, item.mobileLandingUrl),
          mobileLandingUrl: normalizeUrl(item.mobileLandingUrl),
        },
      })),
      ...(filteredButtons.length > 0 ? { buttons: mapButtons(filteredButtons) } : {}),
      ...(couponEnabled ? { couponBook: buildCouponBook() } : {}),
    };
  }

  if (messageType === "CAROUSEL_FEED_MESSAGE") {
    return {
      ...base,
      carousels: images.carouselItems.map((item) => ({
        messageTitle: item.messageTitle.trim(),
        description: item.description.trim(),
        imageUrl: item.imageUploadedUrl,
        shareFlag: item.shareFlag,
        ...(buildCardButtons(item, messageType) ? { buttons: buildCardButtons(item, messageType) } : {}),
        ...(item.couponEnabled
          ? {
              couponBook: {
                couponBookTitleType: item.coupon.couponBookTitleType,
                couponBookTitle: item.coupon.couponBookTitle,
                title: item.coupon.title,
                mobileLandingUrl: normalizeUrl(item.coupon.mobileLandingUrl),
                ...(item.coupon.pcLandingUrl.trim()
                  ? { pcLandingUrl: normalizeUrl(item.coupon.pcLandingUrl) }
                  : {}),
              },
            }
          : {}),
      })),
      ...(moreButtonEnabled
        ? {
            moreButton: {
              pcLandingUrl: resolvePcUrl(moreButton.pcLandingUrl, moreButton.mobileLandingUrl),
              mobileLandingUrl: normalizeUrl(moreButton.mobileLandingUrl),
            },
          }
        : {}),
    };
  }

  // CAROUSEL_COMMERCE_MESSAGE
  return {
    ...base,
    shareFlag,
    ...(commerceIntroEnabled
      ? {
          introCarousel: {
            messageTitle: images.commerceIntro.messageTitle.trim(),
            description: images.commerceIntro.description.trim(),
            imageUrl: images.commerceIntro.imageUploadedUrl,
            landing: {
              pcLandingUrl: resolvePcUrl(images.commerceIntro.pcLandingUrl, images.commerceIntro.mobileLandingUrl),
              mobileLandingUrl: normalizeUrl(images.commerceIntro.mobileLandingUrl),
            },
          },
        }
      : {}),
    carousels: images.carouselItems.map((item) => ({
      messageTitle: item.messageTitle.trim(),
      description: item.description.trim(),
      priceCurrencyCode: "KRW",
      priceAmount: Number(item.priceAmount || "0"),
      ...(item.discountedPriceAmount.trim()
        ? { discountedPriceAmount: Number(item.discountedPriceAmount || "0") }
        : {}),
      imageUrl: item.imageUploadedUrl,
      landing: {
        pcLandingUrl: resolvePcUrl(item.pcLandingUrl, item.mobileLandingUrl),
        mobileLandingUrl: normalizeUrl(item.mobileLandingUrl),
      },
    })),
    ...(moreButtonEnabled
      ? {
          moreButton: {
            pcLandingUrl: resolvePcUrl(moreButton.pcLandingUrl, moreButton.mobileLandingUrl),
            mobileLandingUrl: normalizeUrl(moreButton.mobileLandingUrl),
          },
        }
      : {}),
  };
}
